import { leadPublicCreateAPISchema } from '@hero/api';
import {
  emailSchema,
  financingFleetTierSchema,
  nameSchema,
  phoneSchema,
  type FinancingFleetTier,
} from '@hero/schema';
import { z } from 'zod';
import { useFormUtil, UseFormOptions } from '../utils';

/**
 * Form-shape schema for the public "Book a demo" intake.
 *
 * The endpoint schema (`leadPublicCreateAPISchema`) marks every field
 * `.optional()` so the same wire contract can serve extremely-thin
 * submissions (e.g. an on-ground hunter capturing just a phone). The
 * marketing form has a stricter contract than the endpoint: we require
 * `personName + phone + companyName + fleetSize` (email + description
 * stay optional) so the sales team always has enough to follow up and
 * a fleet-tier bracket to segment on.
 *
 * Field validators are pulled from the shared building blocks
 * (`nameSchema.trim().min(1)`, Saudi-first `phoneSchema`, `emailSchema`,
 * `financingFleetTierSchema`) — the same ones the backend composes into
 * `leadPublicCreateAPISchema`. Any future tightening of those validators
 * flows through automatically.
 *
 * `fleetSize` deliberately reuses the financing profile enum: the same
 * question is only ever asked once across the product, and if the lead
 * converts and applies for financing later the answer carries through.
 *
 * `interests`, `language`, and `sourceMeta` are NOT form inputs — the
 * manager stitches them in at submit time (they're context, not user
 * input).
 */
const emptyToUndefined = <S extends z.ZodTypeAny>(schema: S) =>
  z.preprocess(v => (typeof v === 'string' && v.trim() === '' ? undefined : v), schema);

export const bookDemoFormSchema = z.object({
  personName: nameSchema,
  phone: phoneSchema,
  companyName: nameSchema,
  fleetSize: financingFleetTierSchema,
  email: emptyToUndefined(emailSchema.optional()),
  // Required on the landing form so sales gets context before the
  // first call. The endpoint schema keeps `description` optional so
  // on-ground hunter tools and Zapier imports can still push thin
  // leads without narrative — the marketing form has a stricter
  // contract than the wire. `.trim().min(1)` (rather than a bare
  // `.min(1)`) means whitespace-only submissions get flagged too.
  description: z
    .string()
    .trim()
    .min(1)
    .max(2000),
});

/** Compile-time guard that the form schema's keys are a subset of the
 * endpoint schema's keys — so we can never invent a field that the
 * `/lead/public` endpoint would reject. */
type _FormIsSubsetOfEndpoint =
  keyof typeof bookDemoFormSchema.shape extends keyof typeof leadPublicCreateAPISchema.shape
    ? true
    : never;
const _formFieldsBelongToEndpoint: _FormIsSubsetOfEndpoint = true;
void _formFieldsBelongToEndpoint;

export type BookDemoFormValues = z.infer<typeof bookDemoFormSchema>;

/**
 * String-shaped values bound to the inputs (before Zod parsing turns
 * blanks into `undefined`). React Hook Form works with these directly.
 *
 * `fleetSize` is a `FinancingFleetTier | ''` union because the initial
 * (unselected) `<select>` value is a blank string; Zod parsing rejects
 * that at submit time.
 */
export type BookDemoFormInput = {
  personName: string;
  phone: string;
  email: string;
  companyName: string;
  fleetSize: FinancingFleetTier | '';
  description: string;
};

type UseBookDemoFormOptions = UseFormOptions<BookDemoFormValues>;

export const useBookDemoForm = (options?: UseBookDemoFormOptions) => {
  const defaults: BookDemoFormInput = {
    personName: '',
    phone: '',
    email: '',
    companyName: '',
    fleetSize: '',
    description: '',
    ...(options?.defaultValues as Partial<BookDemoFormInput> | undefined),
  };

  const getResetValues = () => defaults as unknown as Partial<BookDemoFormValues>;

  return useFormUtil({
    zodSchema: bookDemoFormSchema,
    ...options,
    defaultValues: defaults as unknown as Partial<BookDemoFormValues>,
    getResetValues,
  });
};
